import type { PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { XIcon } from '@phosphor-icons/react';

import { Button } from '@shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@shared/ui/dialog';

type VideoQuality = 'low' | 'high';
interface VideoSourceSet {
  webm: string;
  mp4: string;
}

interface LandingProductVideoData {
  preview: VideoSourceSet;
  dialog: VideoSourceSet;
}

interface LandingProductVideoProps {
  title: string;
  video: LandingProductVideoData;
}

interface PreviewCursorPosition {
  x: number;
  y: number;
}

interface NetworkConnection {
  effectiveType?: string;
  saveData?: boolean;
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkConnection;
  mozConnection?: NetworkConnection;
  webkitConnection?: NetworkConnection;
};

const getNetworkConnection = (): NetworkConnection | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  const navigatorWithConnection = navigator as NavigatorWithConnection;

  return (
    navigatorWithConnection.connection ??
    navigatorWithConnection.mozConnection ??
    navigatorWithConnection.webkitConnection
  );
};

const canUpgradeVideoQuality = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.matchMedia('(prefers-reduced-data: reduce)').matches) {
    return false;
  }

  const connection = getNetworkConnection();

  if (connection?.saveData) {
    return false;
  }

  return connection?.effectiveType ? !['slow-2g', '2g'].includes(connection.effectiveType) : true;
};

const getVideoSources = (video: LandingProductVideoData, quality: VideoQuality): VideoSourceSet =>
  quality === 'high' ? video.dialog : video.preview;

const defaultPreviewCursorPosition: PreviewCursorPosition = {
  x: 0,
  y: 0,
};

const watchVideoGlyph = '\u25B6';
const watchVideoLabel = 'Watch video';
const videoUnavailableLabel = 'Video unavailable';

const VideoFallback = () => {
  return (
    <div
      className="
        absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background-4 px-6
        text-center text-foreground-5
      "
    >
      <p className="text-base tracking-tight text-balance">{videoUnavailableLabel}</p>
    </div>
  );
};

export const LandingProductVideo = ({ title, video }: LandingProductVideoProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isPreviewInView, setIsPreviewInView] = useState(false);
  const [isPointerInsidePreview, setIsPointerInsidePreview] = useState(false);
  const [isPreviewFocused, setIsPreviewFocused] = useState(false);
  const [previewQuality, setPreviewQuality] = useState<VideoQuality>('low');
  const [previewHasError, setPreviewHasError] = useState(false);
  const [dialogHasError, setDialogHasError] = useState(false);
  const [previewCursorPosition, setPreviewCursorPosition] = useState(defaultPreviewCursorPosition);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const dialogVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewSources = getVideoSources(video, previewQuality);
  const dialogSources = getVideoSources(video, 'high');
  const isCursorLabelVisible = isPointerInsidePreview || isPreviewFocused;
  const clearPreviewPointerState = useCallback(() => {
    setIsPointerInsidePreview(false);
  }, []);

  useEffect(() => {
    if (!canUpgradeVideoQuality()) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPreviewHasError(false);
      setPreviewQuality('high');
    }, 1400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const previewVideo = previewVideoRef.current;

    if (!previewVideo) {
      return;
    }

    previewVideo.muted = true;
    previewVideo.load();
    previewVideo.pause();
  }, [previewSources.mp4, previewSources.webm]);

  useEffect(() => {
    const previewVideo = previewVideoRef.current;

    if (!previewVideo) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsPreviewInView(true);

      return;
    }

    const startObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry?.isIntersecting) {
          setIsPreviewInView(true);
        }
      },
      {
        threshold: 0.6,
      }
    );

    const stopObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry && !entry.isIntersecting) {
          setIsPreviewInView(false);
        }
      },
      {
        rootMargin: '200% 0px 200% 0px',
        threshold: 0,
      }
    );

    startObserver.observe(previewVideo);
    stopObserver.observe(previewVideo);

    return () => {
      startObserver.disconnect();
      stopObserver.disconnect();
    };
  }, [previewHasError]);

  useEffect(() => {
    const previewVideo = previewVideoRef.current;

    if (!previewVideo) {
      return;
    }

    if (dialogOpen || previewHasError || !isPreviewInView) {
      previewVideo.pause();

      return;
    }

    void previewVideo.play().catch(() => undefined);
  }, [dialogOpen, isPreviewInView, previewHasError, previewSources.mp4, previewSources.webm]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    setDialogHasError(false);

    const dialogVideo = dialogVideoRef.current;

    if (!dialogVideo) {
      return;
    }

    dialogVideo.muted = false;
    void dialogVideo.play().catch(() => undefined);

    return () => {
      dialogVideo.pause();
      dialogVideo.currentTime = 0;
      dialogVideo.muted = true;
    };
  }, [dialogOpen]);

  useEffect(() => {
    const handleWindowBlur = () => {
      clearPreviewPointerState();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearPreviewPointerState();
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearPreviewPointerState]);

  const updatePreviewCursorPosition = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setPreviewCursorPosition({
      x: Math.min(Math.max(event.clientX, bounds.left + 44), bounds.right - 44),
      y: Math.min(Math.max(event.clientY, bounds.top + 32), bounds.bottom - 32),
    });
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <button
        type="button"
        onPointerEnter={(event) => {
          if (event.pointerType === 'touch') {
            return;
          }

          setIsPointerInsidePreview(true);
          updatePreviewCursorPosition(event);
        }}
        onPointerMove={(event) => {
          if (event.pointerType === 'touch') {
            return;
          }

          updatePreviewCursorPosition(event);
        }}
        onPointerLeave={() => {
          clearPreviewPointerState();
        }}
        onPointerCancel={() => {
          clearPreviewPointerState();
        }}
        onFocus={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();

          setIsPreviewFocused(true);
          setPreviewCursorPosition({
            x: bounds.width / 2,
            y: bounds.height / 2,
          });
        }}
        onBlur={() => {
          setIsPreviewFocused(false);
        }}
        onClick={() => {
          setDialogOpen(true);
        }}
        className={[
          `
            group relative flex size-full overflow-hidden rounded-xl bg-black text-left
            outline-hidden
            focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
            focus-visible:ring-offset-background
          `,
          previewHasError && 'cursor-auto',
        ].join(' ')}
        aria-label={`Open ${title} video preview`}
      >
        {!previewHasError && (
          <>
            <video
              ref={previewVideoRef}
              className="size-full object-cover"
              loop
              muted
              playsInline
              preload="auto"
              onError={() => {
                setPreviewHasError(true);
              }}
            >
              <source src={previewSources.webm} type="video/webm" />
              <source src={previewSources.mp4} type="video/mp4" />
            </video>

            <div
              className="
                pointer-events-none fixed z-50 transition-opacity duration-200 will-change-transform
              "
              style={{
                opacity: isCursorLabelVisible ? 1 : 0,
                left: `${String(previewCursorPosition.x)}px`,
                top: `${String(previewCursorPosition.y)}px`,
                transform: 'translate3d(1rem, 1rem, 0)',
              }}
            >
              <span
                className="
                  inline-flex rounded-full bg-background px-4 py-2 text-base font-medium
                  tracking-tight whitespace-nowrap text-foreground shadow-lg
                "
              >
                <span>{watchVideoGlyph}</span>
                <span className="mx-2">{watchVideoLabel}</span>
              </span>
            </div>
            <div
              className="
                absolute inset-0 bg-transparent transition-opacity duration-1000 ease-in-out
                group-hover:opacity-100
                pointer-coarse:opacity-100
                pointer-coarse:inset-shadow-[0_0_2rem_1px_rgba(255,255,255,0.5)]
                pointer-fine:opacity-0 pointer-fine:inset-shadow-[0_0_3rem_0_var(--color-primary)]
              "
            />
          </>
        )}
        {previewHasError && <VideoFallback />}
      </button>

      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black supports-backdrop-filter:backdrop-blur-none"
        className="
          w-[min(96vw,1920px,calc((100dvh-var(--p-offset))*16/9))] max-w-none overflow-hidden
          bg-black p-2 text-white [--p-offset:2rem]
          sm:max-w-none sm:[--p-offset:3rem]
        "
      >
        <DialogTitle className="sr-only">{title} product preview</DialogTitle>
        <DialogDescription className="sr-only">
          Unmuted high-quality video preview for {title}.
        </DialogDescription>

        <div className="overflow-hidden rounded-lg bg-black">
          {!dialogHasError ? (
            <video
              ref={dialogVideoRef}
              className="max-h-[calc(100dvh-3rem)] w-full bg-black object-contain"
              autoPlay
              controls
              playsInline
              preload="auto"
              onError={() => {
                setDialogHasError(true);
              }}
            >
              <source src={dialogSources.webm} type="video/webm" />
              <source src={dialogSources.mp4} type="video/mp4" />
            </video>
          ) : (
            <div
              className="
                flex aspect-video items-center justify-center px-6 text-center text-sm text-white/70
              "
            >
              {videoUnavailableLabel}
            </div>
          )}
        </div>
      </DialogContent>

      {dialogOpen && (
        <DialogClose
          render={
            <Button
              variant="ghost"
              size="icon-lg"
              className="
                fixed top-1 right-1 z-999 size-11 rounded-full bg-black text-white
                backdrop-blur-none
                hover:bg-stone-900 hover:text-white
                md:top-6 md:right-6
              "
            />
          }
        >
          <XIcon className="size-5" />
          <span className="sr-only">Close</span>
        </DialogClose>
      )}
    </Dialog>
  );
};
